import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { PackageData, SingleItemsCouponResponseData } from 'components/modules/patientArea/home/packages/types'
import { uniqBy } from 'lodash'
import { CustomCepData, PackageOrderResponse, ShipmentData } from 'redux/slices/packages-slice'
import { SingleItem } from 'redux/slices/single-items-slice'
import { SubscriptionRecurrence } from 'redux/slices/subscriptions-slice'
import nestApiService from 'services/api/nest-api-service'
import { RecordOfAny } from 'types'
import { dropStaleMounjaroCombos } from 'utils/products/mounjaroPromotion'

export interface CartState {
  packageCart: PackageData[]
  singleItemCart: SingleItem[]
  cepData: CustomCepData | null
  deliveryType: string | null
  deliveryPrice: number
  subscriptionPeriod: SubscriptionRecurrence | null
  shipmentData: ShipmentData[]
  packageOrderResponse: PackageOrderResponse | null
  singleItemsCouponResponseData: SingleItemsCouponResponseData | null
  singleItemsCouponCode: string | null
  isCartDrawerOpen: boolean
  isCheckoutDrawerOpen: boolean
  isConfirmCancelOpen: boolean
  isThankYouOpen: boolean
  isAddressDrawerOpen: boolean
}

const initialState: CartState = {
  packageCart: [],
  singleItemCart: [],
  cepData: null,
  deliveryType: null,
  deliveryPrice: 0,
  subscriptionPeriod: null,
  shipmentData: [],
  packageOrderResponse: null,
  singleItemsCouponResponseData: null,
  singleItemsCouponCode: null,
  isCartDrawerOpen: false,
  isCheckoutDrawerOpen: false,
  isConfirmCancelOpen: false,
  isThankYouOpen: false,
  isAddressDrawerOpen: false
}

export const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    resetCartState: (state) => {
      state = initialState
      return state
    },
    onCartDrawerOpen: (state) => {
      state.isCartDrawerOpen = true
    },
    onCartDrawerClose: (state) => {
      state.isCartDrawerOpen = false
    },
    onCheckoutDrawerOpen: (state) => {
      state.isCheckoutDrawerOpen = true
      // A new checkout starts on the payment form, never on a previous attempt.
      state.packageOrderResponse = null
    },
    onCheckoutDrawerClose: (state) => {
      state.isCheckoutDrawerOpen = false
    },
    onConfirmCancelOpen: (state) => {
      state.isConfirmCancelOpen = true
    },
    onConfirmCancelClose: (state) => {
      state.isConfirmCancelOpen = false
    },
    onThankYouOpen: (state) => {
      state.isThankYouOpen = true
    },
    onThankYouClose: (state) => {
      state.isThankYouOpen = false
    },
    onAddressDrawerOpen: (state) => {
      state.isAddressDrawerOpen = true
    },
    onAddressDrawerClose: (state) => {
      state.isAddressDrawerOpen = false
    },
    closeAllDrawers: (state) => {
      state.isCartDrawerOpen = false
      state.isCheckoutDrawerOpen = false
      state.isConfirmCancelOpen = false
      state.isThankYouOpen = false
      state.isAddressDrawerOpen = false
    },
    setSingleItemCart: (state, action: PayloadAction<SingleItem[]>) => {
      state.singleItemCart = action.payload
    },
    updateSingleItemCart: (state, action: PayloadAction<SingleItem>) => {
      state.singleItemCart = state.singleItemCart.filter((item) => item.productId !== action.payload.productId)
      state.singleItemCart.push(action.payload)
    },
    handleAddSingleItem: (state, action: PayloadAction<SingleItem>) => {
      state.singleItemCart.push(action.payload)
    },
    handleRemoveSingleItem: (state, action: PayloadAction<SingleItem>) => {
      state.singleItemCart = state.singleItemCart.filter((item) => item.id !== action.payload.id)
    },
    handleReplaceSingleItem: (
      state,
      action: PayloadAction<{
        index: number
        item: SingleItem
      }>
    ) => {
      const { index, item } = action.payload

      state.singleItemCart[index] = item
    },
    handleAddPackage: (state, action: PayloadAction<PackageData>) => {
      state.packageCart = [action.payload]
    },
    handleRemovePackage: (state, action: PayloadAction<PackageData>) => {
      state.packageCart = state.packageCart.filter((item) => item.id !== action.payload.id)
    },
    setSubscriptionPeriod: (state, action: PayloadAction<SubscriptionRecurrence | null>) => {
      state.subscriptionPeriod = action.payload
    },
    setShipmentData: (state, action: PayloadAction<ShipmentData[]>) => {
      state.shipmentData = action.payload
    },
    setCepData: (state, action: PayloadAction<CustomCepData | null>) => {
      state.cepData = action.payload
    },
    updateCepData: (state, action: PayloadAction<Partial<CustomCepData>>) => {
      if (state.cepData) state.cepData = { ...state.cepData, ...action.payload }
    },
    setDeliveryType: (state, action: PayloadAction<string | null>) => {
      state.deliveryType = action.payload
    },
    setSingleItemsCouponCode: (state, action: PayloadAction<string | null>) => {
      state.singleItemsCouponCode = action.payload
    },
    // Back to the payment form after the patient drops an unpaid Xis Pix.
    clearPackageOrderResponse: (state) => {
      state.packageOrderResponse = null
    },
    clearSingleItemsCoupon: (state) => {
      state.singleItemsCouponCode = null
      state.singleItemsCouponResponseData = null
    }
  },
  extraReducers: (builder) =>
    builder
      .addCase(applySingleItemCoupon.fulfilled, (state, action) => {
        state.singleItemsCouponResponseData = action.payload
      })
      .addCase(packageOrderCheckout.fulfilled, (state, action) => {
        state.packageOrderResponse = action.payload
      })
      // ECS-879: matched by type so the cart slice does not import home-slice.
      .addMatcher(
        (action): action is PayloadAction<{ singles?: SingleItem[] }> =>
          action.type === 'get-packages-home-v2/fulfilled',
        (state, action) => {
          state.singleItemCart = dropStaleMounjaroCombos(state.singleItemCart, action.payload?.singles ?? [])
        }
      )
})

export const {
  resetCartState,
  onCartDrawerOpen,
  onCartDrawerClose,
  onCheckoutDrawerOpen,
  onCheckoutDrawerClose,
  onConfirmCancelOpen,
  onConfirmCancelClose,
  onThankYouOpen,
  onThankYouClose,
  onAddressDrawerOpen,
  onAddressDrawerClose,
  closeAllDrawers,
  setSingleItemCart,
  updateSingleItemCart,
  handleAddSingleItem,
  handleRemoveSingleItem,
  handleReplaceSingleItem,
  handleAddPackage,
  handleRemovePackage,
  setSubscriptionPeriod,
  setShipmentData,
  setCepData,
  updateCepData,
  setDeliveryType,
  setSingleItemsCouponCode,
  clearSingleItemsCoupon,
  clearPackageOrderResponse
} = cartSlice.actions

export default cartSlice.reducer

interface ApplySingleItemCouponParams {
  couponCode: string
  singleItems: RecordOfAny
  price: number
}

export const applySingleItemCoupon = createAsyncThunk(
  'apply-single-item-coupon',
  async (params: ApplySingleItemCouponParams, { rejectWithValue }) => {
    try {
      const { data } = await nestApiService.apiInstance.post<SingleItemsCouponResponseData>(
        '/package-order/coupon-validation',
        params
      )

      return data
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)

interface PackageOrderCheckoutParams {
  packageOrderData: RecordOfAny
}

export const packageOrderCheckout = createAsyncThunk(
  'package-order-checkout',
  async ({ packageOrderData }: PackageOrderCheckoutParams, { rejectWithValue }) => {
    try {
      const { data } = await nestApiService.apiInstance.post<PackageOrderResponse>('/package-order', packageOrderData)

      return data
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)
